import {useCallback, useEffect, useState} from 'react';

import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import validateProcessFlowchart from 'processes/graph-builder-processes/validation/validateProcessFlowchart';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import buildValidationFlowchartFromStaticData from './buildValidationFlowchartFromStaticData';
import {StaticTemplateFlowchart} from './processFlowchartConfigEditorConstants';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {
  FlowchartConfigAutosaverPausedCondition,
  FlowchartConfigAutosaverPausedConditionType,
} from './redux/reduceFlowchartConfig';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';

// Exporting for testing
export interface UseFlowchartValidationProps {
  draftFlowchartConfig: StaticTemplateFlowchart;
  flowchartConfigId: string;
  flowchartConfigMetadata: FlowchartConfigWithMetadata['metadata'];
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  taskDefinitions: Array<ConfigurableTaskDefinitionGQL>;
  // Note, this must be a memoized callback/plain function to avoid unnecessary re-renders
  setIsFlowchartConfigAutosaverPaused: (
    condition: FlowchartConfigAutosaverPausedConditionType,
    conditionValue: boolean
  ) => void;
  didInitialFlowchartHaveChainedRouters: boolean;
  plugin: ProcessesPluginBase;
}

// Exporting for testing
export interface UseFlowchartValidationReturn {
  validationErrors: Array<ProcessFlowchartValidationError>;
  setValidationErrors: (errors: Array<ProcessFlowchartValidationError>) => void;
}

export function validateFlowchartData(
  staticFlowchartData: StaticTemplateFlowchart,
  flowchartConfigId: string,
  metadata: FlowchartConfigWithMetadata['metadata'],
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  didInitialFlowchartHaveChainedRouters: boolean,
  plugin: ProcessesPluginBase
): Array<ProcessFlowchartValidationError> {
  const {flowchart, designSpace} = buildValidationFlowchartFromStaticData({
    id: flowchartConfigId,
    staticNodesData: staticFlowchartData.nodesStaticData,
    staticEdgesData: staticFlowchartData.edgesStaticData,
    options: {
      schemaMapperConfigs: metadata.schemaMapperConfigs,
      rootTaskSchema,
      customMappingDataByEdgeId: metadata.customMappingDataByEdgeId,
      transitionSourceByNodeIdPair: metadata.transitionSourceByNodeIdPair,
      currentVersionId: metadata.currentVersionId,
    },
  });
  return validateProcessFlowchart({
    flowchart,
    designSpace,
    usingFlowchartFirstConfig: true,
    didInitialFlowchartHaveChainedRouters,
    plugin,
  }).errors;
}

// Module exporting to support test stubbing
export const ValidationModule = {
  validateFlowchartData,
};

export function useFlowchartValidationAndAutosaverControl({
  draftFlowchartConfig,
  flowchartConfigId,
  flowchartConfigMetadata,
  rootTaskSchema,
  setIsFlowchartConfigAutosaverPaused,
  didInitialFlowchartHaveChainedRouters,
  plugin,
}: UseFlowchartValidationProps): UseFlowchartValidationReturn {
  const [validationErrors, setValidationErrors] = useState<Array<ProcessFlowchartValidationError>>(() =>
    ValidationModule.validateFlowchartData(
      draftFlowchartConfig,
      flowchartConfigId,
      flowchartConfigMetadata,
      rootTaskSchema,
      didInitialFlowchartHaveChainedRouters,
      plugin
    )
  );

  useEffect(() => {
    const currentValidationErrors = ValidationModule.validateFlowchartData(
      draftFlowchartConfig,
      flowchartConfigId,
      flowchartConfigMetadata,
      rootTaskSchema,
      didInitialFlowchartHaveChainedRouters,
      plugin
    );
    setValidationErrors(currentValidationErrors);
  }, [
    draftFlowchartConfig,
    flowchartConfigId,
    flowchartConfigMetadata,
    rootTaskSchema,
    didInitialFlowchartHaveChainedRouters,
    plugin,
  ]);

  const setPausedForValidationErrors = useCallback(
    (isPaused: boolean) => {
      setIsFlowchartConfigAutosaverPaused(
        FlowchartConfigAutosaverPausedCondition.HAS_VALIDATION_ERRORS,
        isPaused
      );
    },
    [setIsFlowchartConfigAutosaverPaused]
  );
  useAutosaverPausingForValidationErrors(validationErrors, setPausedForValidationErrors);

  return {
    validationErrors,
    setValidationErrors,
  };
}

const useAutosaverPausingForValidationErrors = (
  validationErrors: Array<ProcessFlowchartValidationError>,
  setAutosaverPaused: (isPaused: boolean) => void
): void => {
  useEffect(() => {
    const blockingErrors = validationErrors.filter((error) => error.blocksPersistence);
    setAutosaverPaused(blockingErrors.length > 0);
  }, [validationErrors, setAutosaverPaused]);
};
